import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Search, Megaphone, Clock, ChevronLeft, ChevronRight, ChevronDown, Ticket, ShoppingBag, Calendar, User, Package } from 'lucide-react';
import producerProfilesApi from 'api/requests/producerProfiles';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR');
}

function formatDateTime(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR');
}

function formatPrice(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function customerEvents(customer) {
    const names = [];
    (customer.purchases || []).forEach(p => {
        if (p.event?.name && !names.includes(p.event.name)) names.push(p.event.name);
    });
    return names;
}

const SORT_OPTIONS = [
    { value: 'recent', label: 'Compra mais recente' },
    { value: 'oldest', label: 'Compra mais antiga' },
    { value: 'most_tickets', label: 'Mais ingressos' },
    { value: 'most_orders', label: 'Mais pedidos' },
];

const PURCHASE_TYPE_OPTIONS = [
    { value: '', label: 'Ingressos e produtos' },
    { value: 'ticket', label: 'Só ingressos' },
    { value: 'product', label: 'Só produtos' },
];

export default function CustomersPage() {
    const [slug, setSlug] = useState(null);
    const [events, setEvents] = useState([]);

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [eventId, setEventId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [purchaseType, setPurchaseType] = useState('');
    const [sort, setSort] = useState('recent');

    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);

    const [expandedEmail, setExpandedEmail] = useState(null);

    const debounceRef = useRef(null);

    useEffect(() => {
        getProducerSlug();
    }, []);

    async function getProducerSlug() {
        try {
            const { data } = await producerProfilesApi.me();
            setSlug(data?.data?.slug || null);
        } catch (err) {
            setError('Erro ao carregar perfil de produtor.');
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!slug) return;
        fetchEventsForFilter();
    }, [slug]);

    async function fetchEventsForFilter() {
        try {
            const [{ data: activeData }, { data: expiredData }] = await Promise.all([
                producerProfilesApi.getEvents(slug, 'active', 1, 50),
                producerProfilesApi.getEvents(slug, 'expired', 1, 50),
            ]);
            const merged = [...(activeData?.data || []), ...(expiredData?.data || [])];
            setEvents(merged);
        } catch (err) {
            // silent — filtro de evento só fica indisponível
        }
    }

    function buildParams(pageValue) {
        const params = { page: pageValue };
        if (search) params.search = search;
        if (eventId) params.event_id = eventId;
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (purchaseType) params.purchase_type = purchaseType;
        if (sort) params.sort = sort;
        return params;
    }

    async function fetchCustomers(pageValue = 1) {
        if (!slug) return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await producerProfilesApi.getCustomers(slug, buildParams(pageValue));
            setCustomers(data?.data || []);
            setPage(data?.meta?.current_page || pageValue);
            setLastPage(data?.meta?.last_page || 1);
            setTotal(data?.meta?.total ?? (data?.data || []).length);
            setExpandedEmail(null);
        } catch (err) {
            setError(err?.response?.data?.message || 'Erro ao carregar clientes.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (slug) {
            fetchCustomers(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, eventId, startDate, endDate, purchaseType, sort]);

    function handleSearchChange(e) {
        const value = e.target.value.slice(0, 100);
        setSearch(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchCustomers(1), 400);
    }

    function goToPage(nextPage) {
        if (nextPage < 1 || nextPage > lastPage) return;
        fetchCustomers(nextPage);
    }

    function toggleRow(email) {
        setExpandedEmail(current => current === email ? null : email);
    }

    return (
        <Page>
            <Section>
                <SectionHeader>
                    <SectionHeaderText>
                        <SectionTitle>Clientes</SectionTitle>
                        {!loading && !error && (
                            <CustomerCount>{total} encontrado{total !== 1 ? 's' : ''}</CustomerCount>
                        )}
                    </SectionHeaderText>

                    <CampaignButton disabled title='Em breve você poderá criar campanhas de e-mail em massa para seus clientes'>
                        <Megaphone size={15} />
                        Criar campanha
                        <ComingSoonBadge>
                            <Clock size={10} />
                            Em breve
                        </ComingSoonBadge>
                    </CampaignButton>
                </SectionHeader>

                <FiltersWrapper>
                    <SearchWrapper>
                        <SearchIcon>
                            <Search size={16} color={colors.nightLight} />
                        </SearchIcon>
                        <SearchInput
                            type="text"
                            placeholder="Buscar por nome ou e-mail..."
                            value={search}
                            onChange={handleSearchChange}
                            maxLength={100}
                        />
                    </SearchWrapper>

                    <FilterRow>
                        <FilterField>
                            <FilterLabel>Evento</FilterLabel>
                            <FilterSelect value={eventId} onChange={e => setEventId(e.target.value)}>
                                <option value=''>Todos os eventos</option>
                                {events.map(ev => (
                                    <option key={ev.id || ev.slug} value={ev.id}>{ev.name}</option>
                                ))}
                            </FilterSelect>
                        </FilterField>

                        <FilterField>
                            <FilterLabel>Comprou</FilterLabel>
                            <FilterSelect value={purchaseType} onChange={e => setPurchaseType(e.target.value)}>
                                {PURCHASE_TYPE_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </FilterSelect>
                        </FilterField>

                        <FilterField>
                            <FilterLabel>De</FilterLabel>
                            <FilterDateInput type='date' value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </FilterField>

                        <FilterField>
                            <FilterLabel>Até</FilterLabel>
                            <FilterDateInput type='date' value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </FilterField>

                        <FilterField>
                            <FilterLabel>Ordenar por</FilterLabel>
                            <FilterSelect value={sort} onChange={e => setSort(e.target.value)}>
                                {SORT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </FilterSelect>
                        </FilterField>
                    </FilterRow>
                </FiltersWrapper>

                {loading && <StateText>Carregando...</StateText>}

                {!loading && error && <StateText $error>{error}</StateText>}

                {!loading && !error && customers.length === 0 && (
                    <EmptyState>
                        <EmptyTitle>Nenhum cliente encontrado</EmptyTitle>
                        <EmptyHint>
                            {search || eventId || purchaseType || startDate || endDate
                                ? 'Tente ajustar a busca ou os filtros.'
                                : 'Seus clientes aparecerão aqui assim que comprarem ingressos ou produtos dos seus eventos.'}
                        </EmptyHint>
                    </EmptyState>
                )}

                {!loading && !error && customers.length > 0 && (
                    <>
                        <TableWrapper>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>Nome</Th>
                                        <Th $hideOnMobile>Eventos</Th>
                                        <Th $hideOnMobile>Ingressos</Th>
                                        <Th $hideOnMobile>Produtos</Th>
                                        <Th $hideOnMobile>Última compra</Th>
                                        <Th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {customers.map((customer, index) => {
                                        const rowKey = customer.email || index;
                                        const expanded = expandedEmail === rowKey;
                                        const eventNames = customerEvents(customer);
                                        const panelId = `customer-panel-${rowKey}`;

                                        return (
                                            <React.Fragment key={rowKey}>
                                                <Tr $expanded={expanded} onClick={() => toggleRow(rowKey)}>
                                                    <Td>
                                                        <CustomerName>{customer.name || '—'}</CustomerName>
                                                        <CustomerEmail>{customer.email}</CustomerEmail>
                                                        <MobileDetails>
                                                            <EventChipsRow>
                                                                {eventNames[0] && <EventChip>{eventNames[0]}</EventChip>}
                                                                {eventNames.length > 1 && <MoreChip>+{eventNames.length - 1} evento{eventNames.length - 1 !== 1 ? 's' : ''}</MoreChip>}
                                                            </EventChipsRow>
                                                            <span>{customer.tickets_count} ingresso{customer.tickets_count !== 1 ? 's' : ''} · {customer.products_count} produto{customer.products_count !== 1 ? 's' : ''}</span>
                                                        </MobileDetails>
                                                    </Td>
                                                    <Td $hideOnMobile>
                                                        <EventChipsRow>
                                                            {eventNames[0] && <EventChip>{eventNames[0]}</EventChip>}
                                                            {eventNames.length > 1 && <MoreChip>+{eventNames.length - 1}</MoreChip>}
                                                            {eventNames.length === 0 && '—'}
                                                        </EventChipsRow>
                                                    </Td>
                                                    <Td $hideOnMobile><CountBadge>{customer.tickets_count}</CountBadge></Td>
                                                    <Td $hideOnMobile><CountBadge>{customer.products_count}</CountBadge></Td>
                                                    <Td $hideOnMobile>{formatDate(customer.last_purchase_at)}</Td>
                                                    <Td align="right">
                                                        <ToggleButton
                                                            type="button"
                                                            $expanded={expanded}
                                                            aria-expanded={expanded}
                                                            aria-controls={panelId}
                                                            onClick={(e) => { e.stopPropagation(); toggleRow(rowKey); }}
                                                        >
                                                            <ChevronDown size={16} />
                                                        </ToggleButton>
                                                    </Td>
                                                </Tr>

                                                {expanded && (
                                                    <ExpandedTr>
                                                        <ExpandedTd colSpan={6} id={panelId}>
                                                            <CustomerDetailPanel customer={customer} />
                                                        </ExpandedTd>
                                                    </ExpandedTr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </TableWrapper>

                        <PaginationBar>
                            <PageInfo>Página {page} de {lastPage}</PageInfo>
                            <PaginationButtons>
                                <PageBtn onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                                    <ChevronLeft size={16} />
                                </PageBtn>
                                <PageBtn onClick={() => goToPage(page + 1)} disabled={page >= lastPage}>
                                    <ChevronRight size={16} />
                                </PageBtn>
                            </PaginationButtons>
                        </PaginationBar>
                    </>
                )}
            </Section>
        </Page>
    );
}

function CustomerDetailPanel({ customer }) {
    const purchases = (customer.purchases || []).slice().reverse();

    return (
        <Panel>
            <Side>
                <SideTitle><User size={14} color={colors.night} />Cliente</SideTitle>

                <InfoRow>
                    <InfoLabel>E-mail</InfoLabel>
                    <InfoValue>{customer.email || '—'}</InfoValue>
                </InfoRow>
                <InfoRow>
                    <InfoLabel>Telefone</InfoLabel>
                    <InfoValue>{customer.phone || '—'}</InfoValue>
                </InfoRow>
                <InfoRow>
                    <InfoLabel>Documento</InfoLabel>
                    <InfoValue>{customer.document || '—'}</InfoValue>
                </InfoRow>
                <InfoRow $emphasis>
                    <InfoLabel>Primeira compra</InfoLabel>
                    <InfoValue>{formatDate(customer.first_purchase_at)}</InfoValue>
                </InfoRow>
                <InfoRow>
                    <InfoLabel>Última compra</InfoLabel>
                    <InfoValue>{formatDate(customer.last_purchase_at)}</InfoValue>
                </InfoRow>
            </Side>

            <Side>
                <SideTitle>
                    <Package size={14} color={colors.night} />
                    Pedidos
                    {customer.orders_count > 0 && <Count>{customer.orders_count}</Count>}
                </SideTitle>

                <PurchaseList>
                    {purchases.map(purchase => (
                        <PurchaseCard key={purchase.order_id}>
                            <PurchaseHeader>
                                <PurchaseEvent>
                                    <Calendar size={13} />
                                    {purchase.event?.name || 'Evento removido'}
                                </PurchaseEvent>
                                <PurchaseDate>{formatDateTime(purchase.created_at)}</PurchaseDate>
                            </PurchaseHeader>

                            <PurchaseItems>
                                {purchase.items.map((item, i) => (
                                    <PurchaseItemRow key={i}>
                                        {item.type === 'ticket'
                                            ? <Ticket size={13} color={panelAccent(colors.occasionalPurple)} />
                                            : <ShoppingBag size={13} color={colors.nightLight} />
                                        }
                                        <ItemName>{item.name}</ItemName>
                                        <ItemQty>x{item.quantity}</ItemQty>
                                        <ItemPrice>{formatPrice(item.price)}</ItemPrice>
                                    </PurchaseItemRow>
                                ))}
                            </PurchaseItems>
                        </PurchaseCard>
                    ))}
                </PurchaseList>
            </Side>
        </Panel>
    );
}

/* ─── Layout ─── */

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Section = styled.div`
    background: #fff;
    border-radius: 14px;
    border: 1px solid ${colors.neutralDark};
    overflow: hidden;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const SectionHeaderText = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const CustomerCount = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
    font-weight: 500;
`;

const CampaignButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    background: ${colors.neutralBackground};
    color: ${colors.nightLight};
    font-size: 13px;
    font-weight: 600;
    cursor: not-allowed;
    opacity: 0.75;
`;

const ComingSoonBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 9px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    padding: 2px 6px;
    border-radius: 8px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

/* ─── Filters ─── */

const FiltersWrapper = styled.div`
    padding: 14px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SearchWrapper = styled.div`
    position: relative;
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    display: flex;
    align-items: center;
`;

const SearchInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 11px 16px 11px 38px;
    font-size: 14px;
    color: ${colors.nightDark};
    background: ${colors.neutralBackground};
    outline: none;
    transition: border-color 0.15s;

    &:focus {
        border-color: ${panelAccent(colors.occasionalPurple)};
        background: #fff;
    }

    &::placeholder {
        color: ${colors.nightLight};
    }
`;

const FilterRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const FilterField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 140px;
    flex: 1;
`;

const FilterLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const FilterSelect = styled.select`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    color: ${colors.nightDark};
    background: #fff;
    outline: none;

    &:focus {
        border-color: ${panelAccent(colors.occasionalPurple)};
    }
`;

const FilterDateInput = styled.input`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    color: ${colors.nightDark};
    background: #fff;
    outline: none;

    &:focus {
        border-color: ${panelAccent(colors.occasionalPurple)};
    }
`;

/* ─── States ─── */

const StateText = styled.p`
    text-align: center;
    color: ${({ $error }) => $error ? colors.danger : colors.nightLight};
    font-size: 14px;
    padding: 40px;
    margin: 0;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px 24px;
    text-align: center;
    gap: 8px;
`;

const EmptyTitle = styled.p`
    font-weight: 600;
    font-size: 15px;
    color: ${colors.nightDark};
    margin: 0;
`;

const EmptyHint = styled.p`
    font-size: 13px;
    color: ${colors.nightLight};
    margin: 0;
`;

/* ─── Table ─── */

const TableWrapper = styled.div`
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const Th = styled.th`
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 12px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
    background: ${colors.neutralBackground};
    white-space: nowrap;

    ${({ $hideOnMobile }) => $hideOnMobile && `
        @media screen and (max-width: ${breakpoints.md}) {
            display: none;
        }
    `}
`;

const Td = styled.td`
    padding: 14px 20px;
    font-size: 14px;
    color: ${colors.nightDark};
    vertical-align: middle;
    text-align: ${({ align }) => align || 'left'};

    ${({ $hideOnMobile }) => $hideOnMobile && `
        @media screen and (max-width: ${breakpoints.md}) {
            display: none;
        }
    `}
`;

const Tr = styled.tr`
    cursor: pointer;
    border-bottom: 1px solid ${colors.neutralDark};
    background: ${({ $expanded }) => $expanded ? '#FAF6FF' : 'transparent'};
    transition: background 0.1s;

    &:hover {
        background: ${({ $expanded }) => $expanded ? '#FAF6FF' : colors.neutralBackground};
    }
`;

const ExpandedTr = styled.tr`
    border-bottom: 1px solid ${colors.neutralDark};
    background: #FAF6FF;
`;

const ExpandedTd = styled.td`
    padding: 0 20px 18px;
`;

const ToggleButton = styled.button`
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: ${colors.nightLight};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s ease, background 0.15s ease;
    transform: rotate(${({ $expanded }) => $expanded ? '180deg' : '0deg'});

    &:hover {
        background: ${colors.neutralBackground};
        color: ${colors.nightDark};
    }
`;

const CustomerName = styled.span`
    font-weight: 600;
    display: block;
`;

const CustomerEmail = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    display: block;
`;

const MobileDetails = styled.div`
    display: none;
    flex-direction: column;
    gap: 6px;
    margin-top: 6px;

    & > span {
        font-size: 12px;
        color: ${colors.nightLight};
    }

    @media screen and (max-width: ${breakpoints.md}) {
        display: flex;
    }
`;

const CountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    color: ${panelAccent(colors.occasionalPurple)};
`;

/* ─── Event chips ─── */

const EventChipsRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
`;

const EventChip = styled.span`
    display: inline-flex;
    align-items: center;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 3px 10px;
    border-radius: 50px;
    font-size: 12px;
    font-weight: 600;
    background: ${colors.neutralBackground};
    border: 1px solid ${colors.neutralDark};
    color: ${colors.nightDark};
`;

const MoreChip = styled.span`
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    padding: 3px 10px;
    border-radius: 50px;
    font-size: 12px;
    font-weight: 600;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    color: ${panelAccent(colors.occasionalPurple)};
`;

/* ─── Pagination ─── */

const PaginationBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-top: 1px solid ${colors.neutralDark};
`;

const PageInfo = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
`;

const PaginationButtons = styled.div`
    display: flex;
    gap: 8px;
`;

const PageBtn = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1.5px solid ${colors.neutralDark};
    background: #fff;
    color: ${colors.nightDark};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: ${colors.neutralBackground};
    }
`;

/* ─── Detail panel (mirrors OrderDetailPanel) ─── */

const Panel = styled.div`
    display: grid;
    gap: 24px;
    padding: 18px;
    background: #FAFAFB;
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;

    @media screen and (min-width: ${breakpoints.smd}) {
        grid-template-columns: 1fr 1fr;
        gap: 32px;
        padding: 20px 22px;
    }
`;

const Side = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const SideTitle = styled.h4`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 8px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const Count = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: 6px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    color: ${panelAccent(colors.occasionalPurple)};
    text-transform: none;
    letter-spacing: 0;
`;

const InfoRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    padding: 7px 0;

    ${({ $emphasis }) => $emphasis && `
        border-top: 1px solid ${colors.neutralDark};
        margin-top: 4px;
        padding-top: 10px;
    `}
`;

const InfoLabel = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const InfoValue = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${colors.nightDark};
    text-align: right;
`;

const PurchaseList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const PurchaseCard = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
`;

const PurchaseHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 14px;
    background: ${colors.neutralBackground};
`;

const PurchaseEvent = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const PurchaseDate = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const PurchaseItems = styled.div`
    display: flex;
    flex-direction: column;
`;

const PurchaseItemRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-top: 1px solid ${colors.neutralDark};
    font-size: 13px;
`;

const ItemName = styled.span`
    flex: 1;
    color: ${colors.nightDark};
`;

const ItemQty = styled.span`
    color: ${colors.nightLight};
`;

const ItemPrice = styled.span`
    font-weight: 600;
    color: ${colors.nightDark};
`;
